/**
 * Date utility functions for calendar and PTO suggestion handling
 *
 * These functions handle timezone-safe date operations to ensure
 * calendar dates are displayed correctly regardless of user timezone.
 */

/**
 * Converts a Date object to ISO date string (YYYY-MM-DD format)
 *
 * @param date - The date to convert
 * @param useUTC - If true, uses UTC date components; if false, uses local date components
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * toISODateString(new Date('2026-01-01T00:00:00.000Z'), true) // "2026-01-01"
 * toISODateString(new Date(2026, 0, 1), false) // "2026-01-01"
 */
export function toISODateString(date: Date, useUTC = false): string {
  if (useUTC) {
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  } else {
    // For local dates (calendar days), use local date components
    // This ensures the displayed date matches what we're comparing
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
}

/**
 * Normalizes an ISO date string (full ISO or date-only) to YYYY-MM-DD format
 *
 * Handles both "2026-01-01T00:00:00.000Z" and "2026-01-01" formats.
 * Extracts the date part directly from the ISO string to avoid timezone conversion issues.
 *
 * @param dateStr - ISO date string (full or date-only)
 * @returns Normalized date string in YYYY-MM-DD format
 *
 * @example
 * normalizeISODateString("2026-01-01T00:00:00.000Z") // "2026-01-01"
 * normalizeISODateString("2026-01-01") // "2026-01-01"
 */
export function normalizeISODateString(dateStr: string): string {
  // If it's already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  // Extract date part directly from ISO string (before 'T' or end of string)
  // This preserves the calendar date regardless of timezone
  const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/)
  if (dateMatch) {
    return dateMatch[1]
  }

  // Fallback: parse as Date and extract UTC date part
  const utcDate = new Date(dateStr)
  return toISODateString(utcDate, true) // Use UTC date to preserve calendar date
}

/**
 * Creates a Set of date strings for a date range (inclusive)
 *
 * @param startDate - Start date (local Date object)
 * @param endDate - End date (local Date object)
 * @returns Set of date strings in YYYY-MM-DD format
 *
 * @example
 * const start = new Date(2026, 0, 1)
 * const end = new Date(2026, 0, 5)
 * createDateRangeSet(start, end) // Set(["2026-01-01", "2026-01-02", "2026-01-03", "2026-01-04", "2026-01-05"])
 */
export function createDateRangeSet(startDate: Date, endDate: Date): Set<string> {
  const dateSet = new Set<string>()
  const currentDate = new Date(startDate)
  const endDateCopy = new Date(endDate)

  while (currentDate <= endDateCopy) {
    dateSet.add(toISODateString(currentDate, false))
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return dateSet
}
